import React, { useContext, useEffect, useState } from 'react'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { selectCompanyWalletCurrencies } from 'store/wallet/wallet.slice'

import {
  BonusCampaignsDto,
  EditCampaignDetailsFormValues,
} from '../../../api/Wallet/Types/MarketingBonusCampaignDto'
import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { successToast, ToastContext } from '../../../components/Toast/Toast'
import { useAppDispatch, useAppSelector } from '../../../store'
import { fetchTickmillCompaniesAction } from '../../../store/identity/identity.actions'
import {
  fetchBonusAvailabilityPeriodsAction,
  fetchBonusCampaignCountdownTriggersAction,
  fetchBonusCampaignExcludeTypesAction,
  fetchBonusLabelsAction,
  fetchWalletCurrenciesByCompanyAction,
  getAllowedBonusTypesAction,
} from '../../../store/wallet/wallet-actions'
import { useApiClient } from '../../../utils/api/ApiClient'
import { IdentityApiClient } from '../../../utils/api/identityApi'
import { WalletsApiClient } from '../../../utils/api/walletsApiClient'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { isEmpty } from '../../../utils/is-empty'
import { CampaignTab } from '../CampaignSlug'
import { BonusCampaignDetailsForm } from './BonusCampaignDetailsForm'
import { BonusCampaignNavigation } from './BonusCampaignNavigation'

interface OuterProps {
  data?: BonusCampaignsDto

  afterSubmit(): void
}

const BonusCampaignDetailsPage: React.FC<OuterProps> = (props) => {
  const { data, afterSubmit } = props

  const walletsApiClient = useApiClient(WalletsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const dateFormatType = auth?.user.dateFormatType.name
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const [availableCurrencies, setAvailableCurrencies] = useState<WalletCurrencyDto[]>([])

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const bonusTypes = useAppSelector((state) => state.wallet.bonusTypes)
  const bonusLabels = useAppSelector((state) => state.wallet.bonusLabels)
  const bonusCampaignExcludeTypes = useAppSelector(
    (state) => state.wallet.bonusCampaignExcludeTypes
  )
  const bonusCampaignAvialabilityPeriods = useAppSelector(
    (state) => state.wallet.bonusCampaignAvailabilityPeriods
  )
  const bonusCampaignCountdownTriggers = useAppSelector(
    (state) => state.wallet.bonusCampaignCountdownTriggers
  )
  const companyWalletCurrencies = useAppSelector(selectCompanyWalletCurrencies)
  const getAvailableCurrencies = (companyIds: number[]): WalletCurrencyDto[] => {
    if (!companyIds?.length || isEmpty(companyWalletCurrencies)) return []

    const currencies = companyIds
      .map((id) => companyWalletCurrencies[id] || [])
      .flat()
      .filter(
        (value, index, self) => index === self.findIndex((currency) => currency.id === value.id)
      )

    return currencies
  }

  useEffect(() => {
    const initializeData = async () => {
      if (isEmpty(bonusTypes)) {
        await dispatch(getAllowedBonusTypesAction(walletsApiClient))
      }

      if (isEmpty(tickmillCompanies)) {
        await dispatch(fetchTickmillCompaniesAction(identityApiClient))
      }
      const hasAnyCurrencies = Object.values(companyWalletCurrencies).some(
        (currencies) => currencies.length > 0
      )
      if (!hasAnyCurrencies) {
        await dispatch(fetchWalletCurrenciesByCompanyAction(walletsApiClient, tickmillCompanies))
      }
      if (isEmpty(bonusCampaignCountdownTriggers)) {
        await dispatch(fetchBonusCampaignCountdownTriggersAction(walletsApiClient))
      }
      if (isEmpty(bonusCampaignAvialabilityPeriods)) {
        await dispatch(fetchBonusAvailabilityPeriodsAction(walletsApiClient))
      }
      if (isEmpty(bonusCampaignExcludeTypes)) {
        await dispatch(fetchBonusCampaignExcludeTypesAction(walletsApiClient))
      }
      if (isEmpty(bonusLabels)) {
        await dispatch(fetchBonusLabelsAction(walletsApiClient))
      }
    }

    initializeData()
  }, [])

  useEffect(() => {
    if (data?.tickmillCompanies && !isEmpty(Object.values(companyWalletCurrencies))) {
      const initialCompanyIds = data.tickmillCompanies.map((c) => c.id)
      const currencies = getAvailableCurrencies(initialCompanyIds)
      setAvailableCurrencies(currencies)
    }
  }, [data?.tickmillCompanies, Object.values(companyWalletCurrencies).length])

  const handleEditSubmit = async (values: EditCampaignDetailsFormValues) => {
    if (data) {
      await walletsApiClient.updateBonusCampaign(data.id, values)
      setToast(successToast('Marketing Bonus Campaign edited successfully.'))
      afterSubmit()
    }
  }

  const handleCompanySelection = (selectedCompanyIds: number[]) => {
    const currencies = getAvailableCurrencies(selectedCompanyIds)
    setAvailableCurrencies(currencies)
  }

  return (
    <>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          bonusDepositId={data?.id}
        />
      )}
      {data && <BonusCampaignNavigation data={data} tab={CampaignTab.DETAILS} />}
      {data && (
        <BonusCampaignDetailsForm
          data={data}
          tickmillCompanies={tickmillCompanies}
          bonusTypes={bonusTypes}
          currencies={availableCurrencies}
          setAuditLogModal={setAuditLogVisible}
          dateFormatType={dateFormatType}
          onSubmit={handleEditSubmit}
          getTickmillCompanyCurrency={handleCompanySelection}
          bonusCampaignAvialabilityPeriods={bonusCampaignAvialabilityPeriods}
          bonusCampaignCountdownTriggers={bonusCampaignCountdownTriggers}
          bonusCampaignExcludeTypes={bonusCampaignExcludeTypes}
          bonusLabels={bonusLabels}
        />
      )}
    </>
  )
}

export default BonusCampaignDetailsPage

export const getUniqueCurrencies = (
  tickmillCompanies: number[],
  companyWalletCurrencies: Record<number, WalletCurrencyDto[]>
): WalletCurrencyDto[] => {
  return tickmillCompanies
    .map((id) => companyWalletCurrencies[id])
    .flat()
    .filter((value, index, self) => self.findIndex((t) => t.id === value.id) === index)
}
